import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { repairCompanion } from "../../lib/companion-install.js";
import { downloadProgressLines } from "../../lib/companion-download-progress.js";
import {
  convergeCompanionFromChannel,
  type CompanionConvergeOutcome,
} from "./install.js";

interface WritableLike {
  write(chunk: string): unknown;
}

export async function runCompanionRepairCommand(
  stdout: WritableLike,
  repair: () => Promise<CompanionConvergeOutcome>,
): Promise<number> {
  const state = await repair();
  if (state.state === "not-released") {
    // Like `companion install`, this reads the channel's `latest` — so it says
    // `latest`. Claiming it read the release this deployment is pinned to would
    // describe a read it never made.
    stdout.write(
      "The latest platform release carries no desktop companions.\n" +
      "There is nothing to repair to — run: m8t platform update\n",
    );
    return 1;
  }
  if (state.state !== "installed") {
    stdout.write("Desktop companions could not be repaired.\n");
    return 1;
  }
  stdout.write(`Desktop companions repaired (${state.version}).\n`);
  return 0;
}

export class CompanionRepairCommand extends M8tCommand {
  static paths = [["companion", "repair"]];
  static usage = Command.Usage({
    description: "Restore the desktop companions and start-at-login state.",
  });

  resourceGroup = Option.String("--resource-group", {
    description: "Which deployment to bind to, when the subscription holds more than one.",
  });

  async executeCommand(): Promise<number> {
    const stdout = this.context.stdout;
    return runCompanionRepairCommand(stdout, () =>
      convergeCompanionFromChannel(repairCompanion, {
        ...(this.resourceGroup !== undefined ? { resourceGroup: this.resourceGroup } : {}),
        // Repair is the command a founder reaches for after an interrupted
        // install, so it is the one most likely to pick up a partial download.
        onDownloadEvent: downloadProgressLines({ write: (line) => stdout.write(line) }),
      }),
    );
  }
}
